/**
 * The operator's calendar day, turned into the two instants the backend filters
 * on.
 *
 * WHY THIS EXISTS AT ALL
 * Order timestamps are rendered with `formatDateTime`, which is
 * `toLocaleString(undefined, …)` — the browser's LOCAL timezone. So an order
 * stored as `2026-08-13T22:30:00Z` is shown to an operator in Ramallah as
 * "14 Aug, 01:30".
 *
 * The date filter used to send calendar dates and the backend read them as UTC
 * days. That order was therefore VISIBLY 14 August and filtered as 13 August:
 * asking for "14 Aug" hid an order the table had just displayed as belonging to
 * it. A filter that disagrees with the column beside it is worse than no
 * filter, because an operator trusts the answer.
 *
 * So the rule is: the calendar day belongs to whoever can see it. This module
 * takes the day the operator picked, works out when that day began and ended
 * WHERE THEY ARE, and sends those two moments. The backend compares instants
 * and holds no opinion about days.
 *
 * DAYLIGHT SAVING
 * `new Date(year, monthIndex, day)` builds a local time and the platform
 * resolves it against the real rules for that date — including the ones that
 * change mid-year. Nothing here adds 24 hours to anything: a day that is 23 or
 * 25 hours long is handled because the NEXT day's midnight is asked for
 * directly rather than computed by arithmetic on the current one. No offset is
 * hardcoded, and no market timezone is assumed; when Store Settings later
 * provides an authoritative store timezone, this is the one place that has to
 * learn about it.
 */

/** "2026-08-14" -> [2026, 8, 14], or null if it is not a calendar day. */
function parts(day: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!match) return null;
  const [, year, month, date] = match;
  return [Number(year), Number(month), Number(date)];
}

/**
 * Local midnight at the start of `day`, as an instant.
 *
 * Deliberately not `new Date("2026-08-14")`: that form is defined to parse as
 * UTC, which is exactly the bug this module exists to remove.
 */
export function localDayStart(day: string): string | undefined {
  const parsed = parts(day);
  if (!parsed) return undefined;
  const [year, month, date] = parsed;
  const start = new Date(year, month - 1, date, 0, 0, 0, 0);
  return Number.isNaN(start.getTime()) ? undefined : start.toISOString();
}

/**
 * Local midnight at the start of the day AFTER `day` — the exclusive upper
 * bound of that day.
 *
 * Asking the calendar for the next day rather than adding 24 hours is what
 * makes this correct across a daylight-saving change and across a month or
 * year boundary: `new Date(2026, 11, 32)` is 1 January 2027, and the platform
 * does that arithmetic, not this function.
 */
export function localDayEnd(day: string): string | undefined {
  const parsed = parts(day);
  if (!parsed) return undefined;
  const [year, month, date] = parsed;
  const next = new Date(year, month - 1, date + 1, 0, 0, 0, 0);
  return Number.isNaN(next.getTime()) ? undefined : next.toISOString();
}
